import React, { useState, useEffect } from 'react';
import { withFormik } from 'formik';
import useLazyRest from 'store/api/useLazyRest';
import {
  AUDIT_LOG_DEFAULT_ACTIONS,
  AUDIT_LOG_ACTIONS_LABEL,
} from 'components/AuditLog/constants';
import Filters from './Filters/Filters';
import List from './List/List';
import './Popup.scss';

const cbn = 'audit-log-popup';
const LIMIT = 10;

type Log = {
  action: string;
  authorId: number;
  authorSnapshot: {
    id: number;
    name: string;
    role: string;
  };
  childKey: string | number | null;
  childType: string | number | null;
  clientId: number | null;
  createdAt: string;
  entityId: number;
  entityType: string;
  id: number;
  message: string;
  referer: string;
  requestId: string;
  updatedAt: string;
};

type Value = {
  action: string;
  excludeBots: boolean;
  limit: any;
  skip: number;
};

type Entity = {
  childKey?: number;
  childType?: string;
  action?: string;
  entityId?: number | string;
  entityType: string;
};

type AuditLogPopupProps = {
  childKey?: number;
  childType?: string;
  entityId?: number | string;
  entityType: string;
  isOpen: boolean;
  values?: {
    action: string;
    excludeBots: boolean;
    limit: number;
    skip: number;
  };
};

const availableActions = AUDIT_LOG_DEFAULT_ACTIONS.map((action) => ({
  value: action,
  label: AUDIT_LOG_ACTIONS_LABEL[action],
}));

const parseParams = (
  {
    limit,
    skip,
    ...filters
  }: { limit?: number; skip?: number; excludeBots?: boolean; search?: string },
  { childType, childKey, action, entityType, entityId }: Entity,
) => {
  const q = {
    childType,
    childKey,
    action,
    entityType,
    entityId,
    ...filters,
  };

  if (q.action === '') {
    delete q.action;
  }

  if (q.search === '') {
    delete q.search;
  }

  if (q.excludeBots === false) {
    delete q.excludeBots;
  }

  return {
    q,
    limit: limit || LIMIT,
    sort: [['created.at', 'desc']],
    skip,
  };
};

function AuditLogPopup({
  childType,
  childKey,
  entityType,
  entityId,
  isOpen,
  values,
}: AuditLogPopupProps) {
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [logs, setLogs] = useState<Log[]>([]);
  const [loadLogs, { error, pending }]: [any, any, any] = useLazyRest({
    url: '/logs',
    method: 'get',
  });

  const entityParams = { childType, childKey, entityType, entityId };

  useEffect(() => {
    async function load() {
      const result = await loadLogs({
        params: parseParams({ ...values }, entityParams),
      });

      if (!result) {
        return;
      }

      if (result.length < values?.limit) {
        setHasMore(false);
      }

      setLogs(result);
    }
    if (isOpen) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, isOpen]);

  async function fetchMore() {
    const newSkip = skip + values?.limit;
    const result: Log[] = await loadLogs({
      params: parseParams({ ...values, skip: newSkip }, entityParams),
    });

    if (result.length < values?.limit) {
      setHasMore(false);
    }

    setLogs((curr: Log[]) => [...curr, ...result]);
    setSkip(newSkip);
  }

  return (
    <div className={cbn}>
      <List
        logs={logs}
        loading={pending}
        fetchMore={fetchMore}
        hasMore={hasMore}
        hasError={Boolean(error)}
      />
      <div className={`${cbn}__filters`}>
        <Filters availableActions={availableActions} />
      </div>
    </div>
  );
}

const enchancedForm = withFormik<AuditLogPopupProps, Value>({
  mapPropsToValues: () => ({
    action: availableActions[0].value,
    excludeBots: true,
    limit: LIMIT,
    skip: 0,
  }),
  handleSubmit: () => null,
});

export default enchancedForm(AuditLogPopup);
